import {queryOptions, useMutation, useQueryClient} from '@tanstack/react-query';
import {api, apiJson, ApiError} from './client';
import {contentResumableEndpoint} from './uploadResumable';
import {useResumableUploadQueue, type ResumableQueueApi} from './useResumableUploadQueue';

// API контракт відповідає `getContentRequestForUser` у бекенді
// (apps/api/src/lib/contentRequests.ts) + DriveListedFile з drive.ts.
// Тип файлу той самий, що у кастомах — у бекенді спільний DriveListedFile.
// Дублюємо інтерфейс свідомо: фронт-код не повинен залежати від типу
// сусідньої підсистеми, інакше при зміні одного зламається й інше.
export interface ContentRequestFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
}

export type ContentRequestState = 'uploading' | 'submitted' | 'published';

export interface ContentRequestView {
  title: string;
  description: string;
  state: ContentRequestState;
  files: ContentRequestFile[];
  // Включён ли перенос файлов по ссылке на Google Диск (LINK_IMPORT_ENABLED на сервере).
  linkImport: boolean;
}

export interface SubmitContentRequestResponse {
  ok: true;
}

// Token у Authorization-хедер, не у query, — щоб не світити в access-логах
// nginx/Caddy. Бекенд приймає обидва способи (див. requireContentRequestToken).
function bearer(token: string): HeadersInit {
  return {Authorization: `Bearer ${token}`};
}

export function contentRequestQueryOptions(id: string, token: string) {
  return queryOptions({
    queryKey: ['content-request', id],
    queryFn: () =>
      api<ContentRequestView>(`/v1/content-requests/${encodeURIComponent(id)}`, {
        headers: bearer(token),
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return false;
      return failureCount < 1;
    },
  });
}

export function useSubmitContentRequest(id: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<SubmitContentRequestResponse>(
        `/v1/content-requests/${encodeURIComponent(id)}/submit`,
        {},
        {headers: bearer(token)},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['content-request', id]});
    },
  });
}

// Тонкий wrapper над спільним useResumableUploadQueue з прив'язкою до
// content-request ендпойнта і Bearer-токеном у заголовках.
export function useContentUploadQueue(id: string, token: string): ResumableQueueApi {
  const qc = useQueryClient();
  return useResumableUploadQueue({
    endpoint: contentResumableEndpoint(id),
    extraHeaders: {Authorization: `Bearer ${token}`},
    onAfterDone: () => {
      void qc.invalidateQueries({queryKey: ['content-request', id]});
    },
  });
}
